import { createMemo, For, Show } from "solid-js";
import { EFFECTIF_MINIMAL } from "../domaine/analyses";
import { croiser } from "../domaine/couverture";
import { definirFacettes, indexer } from "../domaine/facettes";
import { pluriel } from "../format";
import { couleurClasse, type Analyse } from "./analyse";
import { Figure } from "./Figure";

/**
 * Filière × bioagresseur visé : quelles filières n'ont aucun projet sur une
 * classe de bioagresseur. Peu de cases (une dizaine de filières, trois classes),
 * donc lisible ; une filière de moins de trois projets est en retrait, son vide
 * dit surtout qu'elle a peu de projets. Une case pleine filtre le réseau dessus.
 */
export function VueCouverture(props: { analyse: Analyse }) {
  const a = () => props.analyse;
  const p = () => a().portefeuille;
  const croisement = createMemo(() => {
    const c = croiser(
      indexer(p().projets, definirFacettes(p(), a().aujourdhui)),
      "filiere",
      "classe",
    );
    // Colonnes dans l'ordre du référentiel : celui des teintes et de la légende.
    const ids = p().classesBioagresseur.map((x) => x.id);
    const ordre = c.colonnes
      .map((col, k) => ({ k, rang: ids.includes(col.cle) ? ids.indexOf(col.cle) : ids.length }))
      .sort((x, y) => x.rang - y.rang)
      .map((x) => x.k);
    return {
      lignes: c.lignes,
      colonnes: ordre.flatMap((k) => c.colonnes[k] ?? []),
      cases: c.cases.map((ligne) => ordre.map((k) => ligne[k] ?? [])),
    };
  });
  const maxi = createMemo(() =>
    Math.max(
      1,
      ...croisement()
        .cases.flat()
        .map((c) => c.length),
    ),
  );
  const vides = createMemo(
    () =>
      croisement()
        .cases.filter((_, i) => (croisement().lignes[i]?.total ?? 0) >= EFFECTIF_MINIMAL)
        .flat()
        .filter((c) => c.length === 0).length,
  );

  const voir = (filiere: string, classe: string) => {
    a().filtrerSur({
      filiere: { valeurs: [filiere], mode: "un" },
      classe: { valeurs: [classe], mode: "un" },
    });
    a().allerA("graphe");
  };

  return (
    <Show when={croisement().lignes.length && croisement().colonnes.length}>
      <Figure
        id="filiere-bioagresseur"
        titre="Filière × bioagresseur visé"
        lecture={`Projets par filière et classe de bioagresseur ; une case vide : aucun projet. Moins de ${EFFECTIF_MINIMAL} projets : en retrait, trop peu pour conclure. Un clic filtre le réseau.`}
        tableau={() => ({
          colonnes: ["filière", "projets", ...croisement().colonnes.map((c) => c.libelle)],
          lignes: croisement().lignes.map((l, i) => [
            l.libelle,
            l.total,
            ...croisement().colonnes.map((_, j) => croisement().cases[i]?.[j]?.length ?? 0),
          ]),
        })}
      >
        <p class="couverture-bilan" role="status">
          <Show
            when={vides()}
            fallback="Chaque filière assez fournie a au moins un projet par classe."
          >
            <strong>{vides()}</strong> {vides() > 1 ? "combinaisons" : "combinaison"} sans aucun
            projet, parmi les filières d'au moins {EFFECTIF_MINIMAL} projets
          </Show>
        </p>
        <div class="couverture-cadre">
          <table class="couverture">
            <thead>
              <tr>
                <th scope="col" class="couverture-coin">
                  Filière
                </th>
                <For each={croisement().colonnes}>
                  {(c) => (
                    <th scope="col">
                      <span
                        class="pastille"
                        aria-hidden="true"
                        style={{ background: couleurClasse(p(), c.cle) }}
                      />
                      {c.libelle}
                    </th>
                  )}
                </For>
              </tr>
            </thead>
            <tbody>
              <For each={croisement().lignes}>
                {(l, i) => (
                  <tr classList={{ attenuee: l.total < EFFECTIF_MINIMAL }}>
                    <th scope="row">
                      {l.libelle} <span class="couverture-total">{l.total}</span>
                    </th>
                    <For each={croisement().colonnes}>
                      {(c, j) => {
                        const n = () => croisement().cases[i()]?.[j()]?.length ?? 0;
                        return (
                          <td>
                            <button
                              type="button"
                              class="couverture-case"
                              classList={{ vide: n() === 0 }}
                              style={
                                n()
                                  ? { "--intensite": `${Math.round(12 + (n() / maxi()) * 48)}%` }
                                  : undefined
                              }
                              disabled={n() === 0}
                              aria-label={`${l.libelle} × ${c.libelle} : ${n() ? pluriel(n(), "projet") : "aucun projet"}`}
                              title={n() ? "Filtrer le réseau sur ces projets" : "Aucun projet"}
                              onClick={() => voir(l.cle, c.cle)}
                            >
                              {n() || ""}
                            </button>
                          </td>
                        );
                      }}
                    </For>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </Figure>
    </Show>
  );
}
